import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import SEO from '../components/SEO';
import GalleryLightbox from '../components/GalleryLightbox';
import { COMPANY_INFO as STATIC_COMPANY_INFO } from '../data/siteData';
import { useCompanyInfo, usePageData, useGalleryCategories } from '../lib/cms';

export default function AboutPage() {
  const { companyInfo: COMPANY_INFO } = useCompanyInfo();
  const { page } = usePageData('om-cmsnord');
  const { categories } = useGalleryCategories();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeImageIndex, setActiveIndex] = useState(0);

  const heroBg = page.heroImage || '';
  const opgaverCat = categories.find((c) => c.slug === 'opgaver' || c.id === 'opgaver') || categories.find((c) => c.images?.length > 0);
  const galleryImages = opgaverCat?.images || [];

  const openLightbox = (idx) => {
    setActiveIndex(idx);
    setLightboxOpen(true);
  };

  return (
    <>
      <SEO
        title="Om os - CMSNord.dk (tidl. Christians Maskin Service)"
        description="Læs historien om CMSNord.dk / Christians Maskin Service i Brønderslev. Etableret 2022, egne maskiner og professionelt entreprenør- og vognmandsarbejde."
        canonicalUrl={`${window.location.origin}/om-cmsnord`}
        ogImage={heroBg}
      />

      {/* Header Banner */}
      <section className="relative bg-[#121212] text-white py-16 sm:py-24 border-b border-zinc-800 overflow-hidden">
        {heroBg && (
          <div className="absolute inset-0 z-0">
            <img
              src={heroBg}
              alt="Om os"
              className="w-full h-full object-cover opacity-35 mix-blend-luminosity"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#121212]/95 via-[#121212]/80 to-[#121212]/60" />
          </div>
        )}

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <span className="text-[#F4AF00] text-xs font-bold uppercase tracking-widest drop-shadow">
            Virksomhedens Profil
          </span>
          <h1 className="text-3xl sm:text-5xl font-black text-white mt-1 mb-4 uppercase tracking-tight drop-shadow">
            Om CMSNord.dk
          </h1>
          <p className="text-zinc-200 text-base max-w-2xl drop-shadow">
            Lokal forankring, faglig erfaring og stærke tekniske kompetencer i Brønderslev.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-[#F4F4F6] text-[#121212] border-b border-zinc-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7 space-y-6 text-zinc-800 text-base leading-relaxed">
              <div className="bg-white border border-zinc-200 rounded-xl p-6 sm:p-8 space-y-4 shadow-sm">
                <h2 className="text-2xl font-black text-[#121212] uppercase tracking-tight border-b border-zinc-100 pb-3">
                  Virksomhedens Historie &amp; Identitet
                </h2>
                <p className="font-semibold text-zinc-900">
                  <strong>Christians Maskin Service fortsætter under navnet CMSNord.dk.</strong>
                </p>
                <p>
                  Virksomheden blev etableret med en klar vision om at tilbyde ærlig, professionel og lokal entreprenør- og anlægsservice til både erhverv, landbrug og private husstande i Vendsyssel.
                </p>
                <p>
                  Sideløbende med løbende udvikling og udvidelse af maskinparken har virksomheden foretaget en navneændring til <strong>CMSNord.dk</strong> for at styrke brandet og skabe en moderne platform for fremtiden.
                </p>
              </div>

              {/* Quality & Trust */}
              <div className="bg-white border border-zinc-200 rounded-xl p-6 sm:p-8 space-y-4 shadow-sm">
                <h3 className="text-xl font-black text-[#121212] uppercase tracking-tight border-b border-zinc-100 pb-3">
                  Kvalitet, Kundeservice &amp; Pålidelighed
                </h3>
                <p>
                  Vi ved, hvor afgørende det er, at aftaler overholdes og arbejdet udføres i højeste kvalitet. Vi sætter en ære i professionel rådgivning, effektiv udførsel og gennemskuelige aftaler.
                </p>
                <ul className="space-y-2 text-sm text-zinc-700 pt-1">
                  <li className="flex items-start gap-2.5">
                    <span className="w-2 h-2 rounded-full bg-[#F4AF00] mt-1.5 shrink-0" />
                    <span>Mange års praktisk erfaring inden for entreprenør og anlæg</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="w-2 h-2 rounded-full bg-[#F4AF00] mt-1.5 shrink-0" />
                    <span>Lokal forankring og hurtig respons i Brønderslev og omegn</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="w-2 h-2 rounded-full bg-[#F4AF00] mt-1.5 shrink-0" />
                    <span>Kundeorienterede tilbud uden skjulte gebyrer</span>
                  </li>
                </ul>
                <div className="pt-2 flex flex-wrap gap-4">
                  <NavLink
                    to="/ydelser"
                    className="px-5 py-2.5 bg-[#F4AF00] hover:bg-[#D99B00] text-[#121212] font-black text-xs uppercase tracking-wider transition-all rounded-lg shadow-sm flex items-center justify-center"
                  >
                    <span>Se alle vores ydelser</span>
                  </NavLink>
                  <NavLink
                    to="/kontakt"
                    className="px-5 py-2.5 bg-zinc-900 hover:bg-black text-white font-bold text-xs uppercase tracking-wider transition-colors rounded-lg flex items-center justify-center"
                  >
                    <span>Kontakt os i dag</span>
                  </NavLink>
                </div>
              </div>
            </div>

            {/* Info Sidebar */}
            <div className="lg:col-span-5 space-y-6">
              <div className="bg-white border border-zinc-200 rounded-xl p-6 sm:p-8 shadow-sm space-y-4">
                <h3 className="text-xl font-black text-[#121212] uppercase tracking-tight mb-2">Virksomhedsfakta</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between py-2 border-b border-zinc-100">
                    <span className="text-zinc-500 font-semibold">Virksomhedsnavn:</span>
                    <span className="font-bold text-[#121212]">CMSNord.dk</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-zinc-100">
                    <span className="text-zinc-500 font-semibold">Tidligere navn:</span>
                    <span className="text-zinc-800 font-medium">Christians Maskin Service</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-zinc-100">
                    <span className="text-zinc-500 font-semibold">CVR-nummer:</span>
                    <span className="font-mono text-[#121212] font-bold">43233629</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-zinc-100">
                    <span className="text-zinc-500 font-semibold">Adresse:</span>
                    <span className="text-zinc-800 text-right font-medium">Sdr. Omfartsvej 322, 9700 Brønderslev</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-zinc-100">
                    <span className="text-zinc-500 font-semibold">Telefon:</span>
                    <a href="tel:25623505" className="nowrap-contact font-bold text-[#121212] hover:text-[#D99B00]">
                      25 62 35 05
                    </a>
                  </div>
                  <div className="flex justify-between py-2 border-b border-zinc-100">
                    <span className="text-zinc-500 font-semibold">E-mail:</span>
                    <a href="mailto:kontakt@cmsnord.dk" className="nowrap-contact font-bold text-[#121212] hover:text-[#D99B00]">
                      kontakt@cmsnord.dk
                    </a>
                  </div>
                </div>
                <div className="pt-3">
                  <NavLink
                    to="/kontakt"
                    className="w-full py-3 bg-[#F4AF00] hover:bg-[#D99B00] text-[#121212] font-bold text-xs uppercase tracking-wider transition-all rounded-lg shadow-sm flex items-center justify-center"
                  >
                    <span>Kontakt os i dag</span>
                  </NavLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      {galleryImages && galleryImages.length > 0 && (
        <section className="py-16 bg-white text-[#121212] border-b border-zinc-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <span className="text-[#121212] text-xs font-bold uppercase tracking-widest">
                  Galleri
                </span>
                <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-[#121212] mt-1">
                  Billeder fra vores projekter
                </h2>
              </div>
              <NavLink
                to="/galleri"
                className="text-xs text-[#121212] hover:text-[#F4AF00] font-bold uppercase tracking-wider hover:underline flex items-center gap-1.5 transition-colors"
              >
                <span>Se hele galleriet</span>
                <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
              </NavLink>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {galleryImages.slice(0, 4).map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => openLightbox(idx)}
                  className="group relative h-48 sm:h-56 overflow-hidden rounded-xl bg-zinc-100 border border-zinc-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#F4AF00] cursor-pointer"
                >
                  <img
                    src={img.url}
                    alt={img.caption}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-[#121212]/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <i className="fa-solid fa-magnifying-glass-plus text-[#F4AF00] text-xl" aria-hidden="true" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox */}
      {galleryImages.length > 0 && (
        <GalleryLightbox
          images={galleryImages}
          isOpen={lightboxOpen}
          initialIndex={activeImageIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
}
